import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persisted view-mode preference for the Deals list (mirrors Leads — CLAUDE.md §2.3).
 *
 * Stage filter / pagination stay out of the store; they are server-driven query
 * params, not durable user prefs.
 */
export type DealsViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const DEALS_VIEW_MODES: DealsViewMode[] = ['table', 'list', 'cards', 'grouped'];

interface DealsUiState {
  view: DealsViewMode;
  setView: (view: DealsViewMode) => void;
  /** Transient toggle — refreshes default to the active list. */
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-amount`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** Server-side "owner = me" filter (Deal has `owner`). Session-only. */
  mine: boolean;
  setMine: (mine: boolean) => void;
  /** Persisted list filters — survive reloads. */
  stage: string;
  setStage: (stage: string) => void;
  /** Owner (salesperson) filter — a User id as a string; '' = all. */
  owner: string;
  setOwner: (owner: string) => void;
  tag: string;
  setTag: (tag: string) => void;
  createdFrom: string;
  setCreatedFrom: (v: string) => void;
  createdTo: string;
  setCreatedTo: (v: string) => void;
  /** Persisted Pipeline-board filters (owner + created window). */
  boardOwner: string;
  setBoardOwner: (v: string) => void;
  boardCreatedFrom: string;
  setBoardCreatedFrom: (v: string) => void;
  boardCreatedTo: string;
  setBoardCreatedTo: (v: string) => void;
}

export const useDealsUi = create<DealsUiState>()(
  persist(
    (set) => ({
      view: 'table',
      setView: (view) => set({ view }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      mine: false,
      setMine: (mine) => set({ mine }),
      stage: 'all',
      setStage: (stage) => set({ stage }),
      owner: '',
      setOwner: (owner) => set({ owner }),
      tag: 'all',
      setTag: (tag) => set({ tag }),
      createdFrom: '',
      setCreatedFrom: (createdFrom) => set({ createdFrom }),
      createdTo: '',
      setCreatedTo: (createdTo) => set({ createdTo }),
      boardOwner: '',
      setBoardOwner: (boardOwner) => set({ boardOwner }),
      boardCreatedFrom: '',
      setBoardCreatedFrom: (boardCreatedFrom) => set({ boardCreatedFrom }),
      boardCreatedTo: '',
      setBoardCreatedTo: (boardCreatedTo) => set({ boardCreatedTo }),
    }),
    {
      name: 'crm.deals:ui',
      partialize: (s) => ({
        view: s.view,
        stage: s.stage,
        owner: s.owner,
        tag: s.tag,
        createdFrom: s.createdFrom,
        createdTo: s.createdTo,
        boardOwner: s.boardOwner,
        boardCreatedFrom: s.boardCreatedFrom,
        boardCreatedTo: s.boardCreatedTo,
      }),
    },
  ),
);
